import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useGroup } from './GroupContext';
import api from '../services/api';

const NotificationContext = createContext(null);

const POLL_INTERVAL = 30000; // 30 seconds
const PAGE_SIZE = 30;        // notifications loaded per page

// The app owner's inbox, not the person's. This provider is a copy of the one in
// Nucleus and used to poll the same unscoped endpoint with the same token, so the
// developer console played the notification sound for personal events — a share, a
// data read — with no bell anywhere to show what had rung. Naming the surface is
// what stops that; there are no developer-scoped producers yet, so the correct
// result today is an empty list and silence.
const SURFACE = 'developer';

// Preload the notification sound
let notifAudio = null;
try {
  notifAudio = new Audio('/notification.wav');
  notifAudio.volume = 0.4;
} catch (e) {
  // Audio not supported
}

const EMPTY_COUNTS = { lanes: {} };

// Lanes that may raise a desktop notification. The feed never does: "an app read a
// node" is not worth interrupting anyone for.
const LOUD_LANES = new Set(['queue', 'security']);

function permissionState() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

// A desktop notification for what arrived while the tab was not being looked at.
// The OS honours Do Not Disturb for it, which an in-page chime never can.
function announce(rows) {
  if (permissionState() !== 'granted') return;
  if (typeof document !== 'undefined' && !document.hidden) return;
  rows.forEach((n) => {
    try {
      // `tag` collapses the same row announced by two open tabs into one.
      new Notification(n.title || 'ofSelf developer', { body: n.message || '', tag: `notif:${n.id}` });
    } catch { /* a blocked constructor is not a failure */ }
  });
}

function playNotificationSound(soundEnabled) {
  if (!soundEnabled || !notifAudio) return;
  // Clone so overlapping plays don't cut off
  const clone = notifAudio.cloneNode();
  clone.volume = 0.4;
  clone.play().catch(() => {}); // ignore autoplay restrictions
}

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const { activeGroupId } = useGroup();
  // Apps are owned by groups and transfer between them, so a developer inbox is
  // scoped to the space too.
  const space = activeGroupId || 'personal';
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadedCountRef = useRef(0); // how many are currently loaded (for poll refresh + offset)
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem('notif_sound');
      return stored === null ? true : stored === 'true';
    } catch { return true; }
  });
  // Which lane the inbox plate is showing. null is every lane (the badge's view);
  // the plate names one while it is open and hands null back when it closes.
  const [lane, setLane] = useState(null);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [permission, setPermission] = useState(permissionState);
  const rowsRef = useRef([]);
  const seenRef = useRef(new Set());
  const intervalRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const isFirstFetch = useRef(true);
  // Which read may land. Every refresh takes the next number; an answer whose number
  // is no longer the latest is dropped. Switching lane or space starts a refresh, so a
  // slow answer for the lane or space just left can never overwrite the one on screen.
  // A page appended by loadMore is dropped too if any refresh started after it asked:
  // its offset counted rows from a list that has since been replaced.
  const fetchSeqRef = useRef(0);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem('notif_sound', String(next)); } catch {}
      return next;
    });
  }, []);

  // Refresh from the top. Re-fetches at least one page, but as many as are
  // currently loaded, so a poll doesn't discard pages the user already opened.
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const limit = Math.max(PAGE_SIZE, loadedCountRef.current);
      const [data, c] = await Promise.all([
        api.getNotifications({ surface: SURFACE, space, limit, offset: 0, ...(lane ? { lane } : {}) }),
        api.getNotificationCounts({ surface: SURFACE, space }).catch(() => null),
      ]);
      if (seq !== fetchSeqRef.current) return;
      const newNotifs = data.notifications || [];
      const newUnread = data.unread_count || 0;

      // Anything loud and unread that this tab has not seen before gets a desktop
      // notification — never on the first read, which is history, not news.
      const fresh = newNotifs.filter((n) => !seenRef.current.has(n.id));
      newNotifs.forEach((n) => seenRef.current.add(n.id));
      if (!isFirstFetch.current) announce(fresh.filter((n) => !n.is_read && LOUD_LANES.has(n.lane)));

      rowsRef.current = newNotifs;
      setNotifications(newNotifs);
      if (c) setCounts({ lanes: c.lanes || {}, space: c.space || space });
      loadedCountRef.current = newNotifs.length;
      setHasMore(typeof data.total === 'number' ? newNotifs.length < data.total : newNotifs.length >= limit);
      setUnreadCount(newUnread);

      // Play sound if unread count increased (but not on first load)
      if (!isFirstFetch.current && newUnread > prevUnreadRef.current) {
        playNotificationSound(soundEnabled);
      }
      isFirstFetch.current = false;
      prevUnreadRef.current = newUnread;
    } catch (err) {
      // Silently fail — notification polling shouldn't break the app
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [user, space, lane, soundEnabled]);

  // Append the next page.
  const loadMore = useCallback(async () => {
    if (!user) return;
    const seq = fetchSeqRef.current;
    setLoadingMore(true);
    try {
      const data = await api.getNotifications({
        surface: SURFACE, space, limit: PAGE_SIZE, offset: loadedCountRef.current, ...(lane ? { lane } : {}),
      });
      if (seq !== fetchSeqRef.current) return;
      const more = data.notifications || [];
      loadedCountRef.current += more.length;
      more.forEach((n) => seenRef.current.add(n.id));
      rowsRef.current = [...rowsRef.current, ...more];
      setNotifications(prev => [...prev, ...more]);
      setHasMore(typeof data.total === 'number' ? loadedCountRef.current < data.total : more.length >= PAGE_SIZE);
    } catch (err) {
      // Silently fail
    } finally {
      setLoadingMore(false);
    }
  }, [user, space, lane]);

  // One fewer unread in a row's lane — the tab count follows the row.
  const laneLess = useCallback((row) => {
    if (!row || row.is_read || !row.lane) return;
    setCounts((c) => {
      const n = c.lanes?.[row.lane] || 0;
      return n > 0 ? { ...c, lanes: { ...c.lanes, [row.lane]: n - 1 } } : c;
    });
  }, []);

  const markRead = useCallback(async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);
      const row = rowsRef.current.find(n => n.id === notificationId);
      if (row?.is_read) return;
      laneLess(row);
      rowsRef.current = rowsRef.current.map(n => n.id === notificationId ? { ...n, is_read: true } : n);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => {
        const next = Math.max(0, prev - 1);
        prevUnreadRef.current = next;
        return next;
      });
    } catch (err) {
      // Silently fail
    }
  }, [laneLess]);

  const markAllRead = useCallback(async () => {
    try {
      await api.markAllNotificationsRead({ surface: SURFACE, space });
      rowsRef.current = rowsRef.current.map(n => ({ ...n, is_read: true }));
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setCounts(c => ({ ...c, lanes: Object.fromEntries(Object.keys(c.lanes || {}).map(k => [k, 0])) }));
      setUnreadCount(0);
      prevUnreadRef.current = 0;
    } catch (err) {
      // Silently fail
    }
  }, [space]);

  // Sweep one feed row away. The server refuses anything else (409), and a refusal
  // leaves the row standing — a dismissal that did not land must not look like one.
  const dismiss = useCallback(async (notificationId) => {
    try {
      await api.dismissNotification(notificationId);
      const row = rowsRef.current.find(n => n.id === notificationId);
      rowsRef.current = rowsRef.current.filter(n => n.id !== notificationId);
      loadedCountRef.current = Math.max(0, loadedCountRef.current - 1);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (row && !row.is_read) {
        laneLess(row);
        setUnreadCount(prev => {
          const next = Math.max(0, prev - 1);
          prevUnreadRef.current = next;
          return next;
        });
      }
    } catch {
      // Refused (409) or offline: the row stays.
    }
  }, [laneLess]);

  // Ask for desktop notifications. Only ever from a press, next to the reason —
  // a prompt on load is refused once and then unavailable for good.
  const askPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    try {
      setPermission(await Notification.requestPermission());
    } catch {
      setPermission(permissionState());
    }
  }, []);

  // Start/stop polling based on user login state
  useEffect(() => {
    if (user) {
      isFirstFetch.current = true;
      fetchNotifications();
      intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL);
    } else {
      fetchSeqRef.current += 1; // nothing still in flight lands after sign-out
      setNotifications([]);
      rowsRef.current = [];
      seenRef.current = new Set();
      setCounts(EMPTY_COUNTS);
      setUnreadCount(0);
      prevUnreadRef.current = 0;
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, fetchNotifications]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      loadingMore,
      hasMore,
      soundEnabled,
      toggleSound,
      fetchNotifications,
      loadMore,
      markRead,
      markAllRead,
      dismiss,
      lane,
      setLane,
      counts,
      permission,
      askPermission,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
